import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { ACCESS_TOKEN, API_URL } from "../../constants";
import Cookie from "../../utils/cookie";
import { BrandContext } from "../Brand/context";
import useDidMountEffect from "../../hooks/useDidMountEffect";
import ReconnectingWebSocket from "../../utils/reconnecting-websocket";
import { WebSocketContext } from "./context";

export const WebSocketState = ({ children }: { children: React.ReactNode }) => {
  const { brandId, isBrandUser } = useContext(BrandContext);
  const websocket = useRef<ReconnectingWebSocket | null>(null);
  //TODO change data logic to dataObject logic in whole app
  const [data, setData] = useState<unknown>();
  const [dataObject, setDataObject] = useState<Record<string, unknown>>({});
  const [budtenderOrdersCount, setBudtenderOrdersCount] = useState(0);
  const [notification, setNotification] = useState<unknown>();
  type WebSocketHandler = (...args: unknown[]) => void;
  const handlers = useRef<
    Record<string, { handler: WebSocketHandler; trigger: string }>
  >({});

  const addHandler = useCallback(
    (id: string, handler: WebSocketHandler, trigger: string) => {
      handlers.current = { ...handlers.current, [id]: { handler, trigger } };
    },
    []
  );

  const removeHandler = useCallback((id: string) => {
    const { [id]: _, ...rest } = handlers.current;
    handlers.current = rest;
  }, []);

  const ping = () => {
    if (!websocket.current) return;
    if (websocket.current.readyState !== ReconnectingWebSocket.OPEN) return;
    websocket.current.send("ping");
    setTimeout(ping, 15 * 1000);
  };

  const openWebsocket = (brandId) => {
    const token = Cookie.get(ACCESS_TOKEN);

    if (!token) return;

    if (
      !!websocket.current &&
      websocket.current.readyState !== ReconnectingWebSocket.CLOSED
    ) {
      websocket.current.close();
    }

    const protocol = API_URL.includes("https") ? "wss" : "ws";
    const host = API_URL.replace("https://", "").replace("http://", "");
    const websocketPath = `${protocol}://${host}/websocket?isAdminPanel&token=${
      "Bearer " + token
    }${
      brandId ? "&brandId=" + brandId : ""
    }&timezone=${new Date().getTimezoneOffset()}`;
    websocket.current = new ReconnectingWebSocket(websocketPath, null, {
      timeoutInterval: 500,
      reconnectInterval: 1000,
    });

    websocket.current.onopen = () => {
      console.log("Connection open");
      ping();
    };
    websocket.current.onclose = () => {
      console.log("Connection closed");
    };
    websocket.current.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.event === "notification_status") {
        setNotification({
          ...data?.data,
          data: JSON.parse(data?.data?.data),
        });
      } else if (data.event === "brand_budtender_ordersCount") {
        setBudtenderOrdersCount(data.data.counts);
      } else {
        setData(data.data);
      }
      setDataObject((state) => ({ ...state, [data.event]: data.data }));
      for (const id in handlers.current) {
        const trigger = !!handlers.current[id] && handlers.current[id].trigger;
        if (!trigger || trigger === data.event)
          handlers.current[id]?.handler &&
            handlers.current[id].handler(data.data);
      }
    };
    return () => {
      websocket.current.close();
    };
  };

  useDidMountEffect(() => {
    if (isBrandUser === null) return;
    return openWebsocket(brandId);
  }, [brandId, isBrandUser]);

  const value = useMemo(
    () => ({
      data,
      notification,
      budtenderOrdersCount,
      addHandler,
      removeHandler,
      dataObject,
    }),
    [
      data,
      notification,
      budtenderOrdersCount,
      addHandler,
      removeHandler,
      dataObject,
    ]
  );

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
